/**
 * Saleor JWT `iss` equals the GraphQL endpoint URL string exactly.
 * A trailing-slash mismatch (`/graphql` vs `/graphql/`) causes:
 * "Token's `iss` and request URL do not match" — no Authorization header on client requests.
 *
 * Use the same normalization for `NEXT_PUBLIC_SALEOR_API_URL`, `SALEOR_API_URL`, and auth-sdk.
 */
export function normalizeSaleorGraphqlUrl(url: string): string {
	const trimmed = url.trim();
	if (!trimmed) return trimmed;
	const withoutTrailingSlashes = trimmed.replace(/\/+$/, "");
	return `${withoutTrailingSlashes}/`;
}

/**
 * Hosts, die nur innerhalb des Servers erreichbar sind: ein Name ohne Punkt
 * (Docker-Dienst `api` im Netz saleor-backend-tier, `localhost`), Loopback oder
 * ein privater IPv4-Bereich. Nur dorthin ist Klartext-HTTP erlaubt. Ein JWKS per
 * Klartext aus dem Internet liesse jeden auf dem Weg eigene Schluessel
 * unterschieben, und damit signierte Zahlungs-Webhooks gingen durch.
 */
export function isServerInternalHost(hostname: string): boolean {
	if (hostname === "[::1]") return true;
	if (!hostname.includes(".")) return true;
	const octets = hostname.split(".").map((part) => (/^\d{1,3}$/.test(part) ? Number(part) : Number.NaN));
	if (octets.length !== 4 || octets.some((octet) => !(octet >= 0 && octet <= 255))) return false;
	const [a, b] = octets;
	return a === 127 || a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

/**
 * Pruefung fuer `SALEOR_API_INTERNAL_URL`, den Saleor-Endpunkt im Docker-Netz fuer
 * oeffentliche Queries (siehe graphql.ts). http oder https ohne Zugangsdaten, ein
 * interner Host (auch bei https: diese Adresse bekommt `X-Forwarded-Proto: https`,
 * eine Behauptung, die an keinen fremden Host gehoert) und genau der Pfad
 * `/graphql/`. Liefert die normalisierte URL oder nur den Grund der Ablehnung,
 * nie den Wert: Aufrufer protokollieren ausschliesslich den Grund.
 */
export function parseSaleorInternalApiUrl(raw: string): { url: string } | { problem: string } {
	let parsed: URL;
	try {
		parsed = new URL(normalizeSaleorGraphqlUrl(raw));
	} catch {
		return { problem: "not an absolute URL" };
	}
	if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return { problem: "not http(s)" };
	if (parsed.username || parsed.password) return { problem: "credentials in URL" };
	// Ein IPv6-Literal hat keinen Punkt und gilt in isServerInternalHost als Name;
	// hier zaehlt davon nur Loopback (die JWKS-Regel bleibt unveraendert).
	const ipv6Literal = parsed.hostname.startsWith("[");
	if (ipv6Literal ? parsed.hostname !== "[::1]" : !isServerInternalHost(parsed.hostname))
		return { problem: "not an internal host" };
	if (parsed.pathname !== "/graphql/" || parsed.search || parsed.hash)
		return { problem: "path is not /graphql/" };
	return { url: parsed.href };
}

/** Hostnames that can only be this machine. `localhost` is deliberately absent, see below. */
const LOOPBACK_HOSTNAMES = new Set(["127.0.0.1", "[::1]", "::1"]);

/**
 * Extra headers a plain-HTTP loopback Saleor endpoint needs.
 *
 * Saleor runs with `ENABLE_SSL: "True"` in production (`docker-compose.prod.yml`),
 * which turns on Django's `SECURE_SSL_REDIRECT`. A plain-HTTP request to
 * `http://127.0.0.1:8000/graphql/` is answered with `301 -> https://127.0.0.1:8000/`,
 * a port that speaks no TLS. fetch follows that redirect, downgrades the POST to
 * a GET on the way (301 semantics) and then dies in the TLS handshake. Measured
 * on the production host, 2026-09-20: 301 without this header, 200 with it.
 *
 * Django trusts `X-Forwarded-Proto` because Saleor sets `SECURE_PROXY_SSL_HEADER`;
 * that is the same header nginx injects on the public path, so this makes the
 * loopback request look exactly like the one that arrives through Cloudflare.
 * `deploy.sh` has used the same header in its own loopback curls all along.
 *
 * Only for the literal loopback address over plain HTTP. `localhost` is excluded
 * on purpose: that is the local development endpoint, where Saleor runs with
 * ENABLE_SSL off, no redirect happens, and claiming a secure request would change
 * `request.is_secure()` for no gain.
 *
 * Nothing in production takes this path: `deploy.sh` gives the runtime container
 * the public HTTPS URL with `docker run --env`, so at runtime this returns {}.
 */
export function saleorLoopbackHeaders(apiUrl: string | null | undefined): Record<string, string> {
	if (!apiUrl) return {};
	let parsed: URL;
	try {
		parsed = new URL(apiUrl);
	} catch {
		return {};
	}
	if (parsed.protocol !== "http:") return {};
	if (!LOOPBACK_HOSTNAMES.has(parsed.hostname)) return {};
	return { "x-forwarded-proto": "https" };
}
